import React, { memo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { get, size, isEqual, isUndefined } from 'lodash';

import { LLFormat, LLLFormat } from '../../../common/services/ValidationRules';
import SSPList from '../../../common/components/SSPList/index';

import {
  makeDebounceOnTextFilter,
  onDatePickerFilter,
  onDatePickerFilterTimeStampOutput,
} from '../../../common/components/CommonTableDataFilter/CommonTableDataFilter';

import {
  chargerStatusOptions,
  chargingOptions,
  conditionOptions,
  configurationOptions,
  getColumnClasses,
  getProductTypesColumn,
  onlineOptions,
} from '../../../common/components/CommonTableColumns';
import { permissions } from '../../../common/constants/permissions';

import { getCountryByCode } from '../../AdminTools/components/CountryUtils';

import { CustomerTableSelect, CustomerURLCell, IdCell, PlazaTableSelect, TableSelect } from './ChargersTableComponents';
import CountryTableSelect from './CountryTableSelect';
import { chargerConfigurationStatus, getDictionaryValues } from '../constants';
import { FILTER_MAX_LENGTH } from '../../../common/constants/listFilters';
import uiLabelsPaths from '../../../common/services/uiFieldLabelsDictionary';
import { fullPlazaNameLabel } from '../../../common/constants/naming';
import { CHARGER_ONLINE_NO, CHARGER_ONLINE_YES, CHARGER_STATUS_UNKNOWN } from '../../../common/constants/statuses';
import { isOnlyMobile } from '../../../common/services/isMobile';
import { StatusLabel, chargerStateLabels } from '../../../common/constants/CommonUsedDropdownsStatics';

export const chargersInfoTableId = 'chargersList';

export const chargersListDefaultSorting = { id: 'name', desc: false };

const getChargerIdCell = ({ openChangerModal, source, user }, value) =>
  openChangerModal ? (
    <p
      className="link centered"
      onClick={() => {
        openChangerModal({ id: value });
      }}
    >
      {value}
    </p>
  ) : (
    <IdCell source={source} value={value} permissions={user.currentPermissions} />
  );

const getCustomerNameCell = (user, { original, value }) => {
  if (!original.customer.url) return null;

  return <CustomerURLCell customer={original.customer} chargerId={original.c_uuid} value={value} user={user} />;
};

const getDefaultColumns = (props) => {
  const { isFilterApplied, productTypes, advancedSearchEnabled, openChangerModal, source, user, config } = props;
  const uiLabels = get(config, 'ui_labels', {});
  const productTypesValues = getDictionaryValues(productTypes);

  return [
    {
      id: 'c_uuid_contains',
      minWidth: 260,
      Header: get(uiLabels, uiLabelsPaths.c_uuid, 'Charger ID'),
      accessor: 'c_uuid',
      ...getColumnClasses({ isLink: true, isSticky: !isOnlyMobile }),
      fixed: true,
      Cell: ({ value }) => getChargerIdCell({ openChangerModal, source, user }, value),
      Filter: makeDebounceOnTextFilter(800, { other: { inputProps: { maxLength: FILTER_MAX_LENGTH } } }),
    },
    {
      Header: 'Date/Time (UTC)',
      minWidth: 180,
      id: 'time',
      accessor: 'update_reason_timestamp',
      Cell: ({ value }) => (value ? moment.utc(value).format(LLLFormat) : ''),
      Filter: onDatePickerFilter,
    },
    getProductTypesColumn(productTypesValues, isFilterApplied, uiLabels),
    {
      Header: () => get(uiLabels, uiLabelsPaths.c_name, 'Charger Name'),
      id: 'name',
      accessor: 'c_identity.c_name',
      minWidth: 200,
      Filter: makeDebounceOnTextFilter(800, { other: { inputProps: { maxLength: FILTER_MAX_LENGTH } } }),
    },
    {
      Header: () => get(uiLabels, uiLabelsPaths.c_country, 'Country'),
      id: 'country',
      accessor: 'c_identity.c_country',
      Cell: ({ value }) => getCountryByCode(value),
      Filter: ({ filter, onChange }) => <CountryTableSelect onChange={onChange} value={filter ? filter.value : ''} />,
      minWidth: 200,
    },
    {
      Header: () => get(uiLabels, uiLabelsPaths.c_address, 'Address'),
      id: 'address',
      accessor: 'c_identity.c_address',
      minWidth: 200,
      Filter: makeDebounceOnTextFilter(800, { other: { inputProps: { maxLength: FILTER_MAX_LENGTH } } }),
    },
    {
      Header: () => get(uiLabels, uiLabelsPaths.c_contact, 'Contact'),
      id: 'contact',
      accessor: 'c_exploitation_info.c_contact',
      minWidth: 100,
      Filter: makeDebounceOnTextFilter(800, { other: { inputProps: { maxLength: FILTER_MAX_LENGTH } } }),
    },
    {
      Header: () => get(uiLabels, uiLabelsPaths.c_warranty_end, 'Warranty End (UTC)'),
      id: 'warranty_end',
      accessor: 'c_exploitation_info.c_warranty_end',
      Cell: ({ value }) => (value ? moment(value).utc().format(LLFormat) : ''),
      minWidth: 180,
      Filter: onDatePickerFilterTimeStampOutput,
    },
    {
      Header: () => get(uiLabels, uiLabelsPaths.c_status, 'Charger Status'),
      id: 'status',
      minWidth: 210,
      accessor: (charger) => {
        const status = get(charger, 'i2p2_state.c_status', '');
        const isOnline = get(charger, 'i2p2_state.c_online');
        const value = status && status.toLowerCase();

        return isOnline ? value : 'unknown';
      },
      Cell: ({ value }) => <StatusLabel status={value} />,
      Filter: ({ filter, onChange }) => (
        <TableSelect options={chargerStatusOptions} onChange={onChange} value={filter ? filter.value : ''} />
      ),
    },
    {
      Header: () => get(uiLabels, uiLabelsPaths.connector_state, 'Present Charger State'),
      id: 'charger_state',
      accessor: (charger) => {
        const chargerState = get(charger, 'charger_state');
        const isOnline = get(charger, 'i2p2_state.c_online');

        return isOnline ? chargerState : CHARGER_STATUS_UNKNOWN.toUpperCase();
      },
      minWidth: 180,
      filterable: !isFilterApplied,
      Filter: ({ filter, onChange }) => <TableSelect options={chargingOptions} onChange={onChange} value={filter ? filter.value : ''} />,
      Cell: ({ value }) => <StatusLabel status={chargerStateLabels[value]} />,
    },
    {
      Header: () => get(uiLabels, uiLabelsPaths.c_condition, 'Charger Condition'),
      id: 'condition',
      accessor: 'i2p2_state.c_condition',
      minWidth: 140,
      filterable: !isFilterApplied,
      Filter: ({ filter, onChange }) => <TableSelect options={conditionOptions} onChange={onChange} value={filter ? filter.value : ''} />,
      Cell: ({ value }) => <StatusLabel status={value} />,
    },
    {
      Header: () => get(uiLabels, uiLabelsPaths.product_serial, 'Product Serial Number'),
      id: 'product_serial_number',
      className: 'nested-table',
      accessor: 'c_identity.product_serial_number',
      minWidth: 180,
      Filter: makeDebounceOnTextFilter(800, { other: { inputProps: { maxLength: FILTER_MAX_LENGTH } } }),
    },
    {
      Header: () => get(uiLabels, uiLabelsPaths.c_online, 'Online'),
      id: 'online',
      minWidth: 100,
      accessor: (d) => {
        const isOnline = d.i2p2_state && !!d.i2p2_state.c_online;

        return isOnline ? CHARGER_ONLINE_YES : CHARGER_ONLINE_NO;
      },
      Cell: ({ value }) => <StatusLabel status={value} />,
      Filter: ({ filter, onChange }) => <TableSelect options={onlineOptions} onChange={onChange} value={filter ? filter.value : ''} />,
    },
    {
      Header: () => 'FW Version',
      id: 'fw_version',
      accessor: 'fw_version',
      minWidth: 180,
      Filter: makeDebounceOnTextFilter(800, { other: { inputProps: { maxLength: FILTER_MAX_LENGTH } } }),
      filterable: !advancedSearchEnabled,
    },
    {
      Header: () => get(uiLabels, uiLabelsPaths.configuration, 'Configuration'),
      id: chargerConfigurationStatus,
      minWidth: 150,
      accessor: chargerConfigurationStatus,
      Cell: ({ value }) => <StatusLabel status={value} />,
      Filter: ({ filter, onChange }) => (
        <TableSelect options={configurationOptions} onChange={onChange} value={filter ? filter.value : ''} />
      ),
      filterable: !advancedSearchEnabled,
    },
    {
      Header: () => fullPlazaNameLabel,
      id: 'plaza_uuid',
      minWidth: 180,
      accessor: 'plaza_name',
      Filter: ({ filter, onChange }) => <PlazaTableSelect onChange={onChange} value={filter ? filter.value : ''} />,
    },
  ];
};

const getCustomerNameColumn = (user) => ({
  Header: () => 'Customer Name',
  id: 'customer_id',
  minWidth: 180,
  accessor: 'customer.customerName',
  Cell: (cellProps) => getCustomerNameCell(user, cellProps),
  Filter: ({ filter, onChange }) => <CustomerTableSelect onChange={onChange} value={filter ? filter.value : ''} />,
});

const ChargersList = memo(
  ({
    id,
    advancedSearchEnabled,
    isFilterApplied,
    getData,
    columnsConfig,
    exportToCSV,
    exportLimit,
    config,
    productTypes,
    openChangerModal,
    source,
    user,
  }) => {
    const [reconfiguredColumns, setReconfiguredColumns] = useState([]);
    const [prevColumnsConfig, setPrevColumnsConfig] = useState(null);
    const [prevAdvancedSearchEnabled, setPrevAdvancedSearchEnabled] = useState(null);

    useEffect(() => {
      if ((size(columnsConfig) && columnsConfig !== prevColumnsConfig) || advancedSearchEnabled !== prevAdvancedSearchEnabled) {
        const defaultColumns = getDefaultColumns({
          config,
          isFilterApplied,
          productTypes,
          advancedSearchEnabled,
          openChangerModal,
          source,
          user,
        });

        if (user.currentPermissions && user.currentPermissions.includes(permissions.SUPERVISOR)) {
          defaultColumns.push(getCustomerNameColumn(user));
        }

        const reconfiguredColumnsTemp = [defaultColumns[0]];

        for (const configField of columnsConfig) {
          const { id } = configField;
          const column = defaultColumns.find((column) => column.id === id);

          if (column) {
            column.show = isUndefined(configField.show) ? true : configField.show;
            reconfiguredColumnsTemp.push(column);
          }
        }

        setReconfiguredColumns(reconfiguredColumnsTemp);
        setPrevColumnsConfig(columnsConfig);
        setPrevAdvancedSearchEnabled(advancedSearchEnabled);
      }
    }, [columnsConfig, advancedSearchEnabled, config, isFilterApplied, productTypes, openChangerModal, source, user]);

    reconfiguredColumns.forEach((column) => {
      column.filterable = !isFilterApplied;
    });

    return (
      <div className="chargers-table">
        <SSPList
          checkboxSticky
          mobileSticky
          onGetDataAction={getData}
          defaultPageSize={20}
          defaultSorted={chargersListDefaultSorting} // is used manually inside
          id={id}
          columns={reconfiguredColumns}
          multiselect
          exportable
          onExport={exportToCSV}
          exportLimit={exportLimit}
          filterable={!isFilterApplied}
        />
      </div>
    );
  },
  (prevProps, nextProps) =>
    isEqual(prevProps.data, nextProps.data) &&
    isEqual(prevProps.activeChargerIds, nextProps.activeChargerIds) &&
    isEqual(prevProps.columnsConfig, nextProps.columnsConfig) &&
    isEqual(prevProps.config, nextProps.config) &&
    isEqual(prevProps.id, nextProps.id) &&
    isEqual(prevProps.advancedSearchEnabled, nextProps.advancedSearchEnabled) &&
    isEqual(prevProps.productTypes, nextProps.productTypes) &&
    isEqual(prevProps.isFilterApplied, nextProps.isFilterApplied),
);

ChargersList.propTypes = {
  exportToCSV: PropTypes.func,
  getData: PropTypes.func,
  advancedSearchEnabled: PropTypes.bool,
  isFilterApplied: PropTypes.bool,
};

export default ChargersList;
